import { MessageDto, MessageType } from 'common/dto-types/message-dto';
import { MessageFormFields, defaultInitialMessageFormValue } from './dto';
import { MessageArticleValidationStatus } from 'components/contentful-article-search-field/dto';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { FormikHelpers } from 'formik';

/**
 * Shows a success toast notification
 */
export const showSuccessToast = (message: string) => {
    dispatch(
        openSnackbar({
            open: true,
            message,
            variant: 'alert',
            alert: {
                color: 'success',
            },
        }),
    );
};

/**
 * Shows an error toast notification
 */
export const showErrorToast = (message: string) => {
    dispatch(
        openSnackbar({
            open: true,
            message,
            variant: 'alert',
            alert: {
                color: 'error',
            },
        }),
    );
};

/**
 * Handles API errors consistently across all message actions
 * - Maps field-level validation errors to Formik
 * - Shows appropriate toast notifications
 * - Manages Formik submitting state
 */
export const handleApiError = (err: any, actionName: string, formikHelpers?: FormikHelpers<MessageFormFields>) => {
    // Check if it's a 400 validation error with field-specific errors
    if (err.status === 400 && err.data?.errors) {
        const fieldErrors = mapBackendValidationErrors(err.data.errors);

        if (formikHelpers) {
            formikHelpers.setErrors(fieldErrors);
        }

        showErrorToast('Please fix the validation errors in the form');
    } else {
        // Generic error for non-validation errors
        const errorMessage =
            err.response?.data?.message || err.message || "There's an error that happened when processing your request";
        showErrorToast(errorMessage);
    }

    // Reset submitting state
    if (formikHelpers) {
        formikHelpers.setSubmitting(false);
    }
};

/**
 * Converts backend MessageDto to form field values
 * Backend returns messageTarget with groups[] and users[] arrays (full DTOs)
 * Form needs these objects to populate the form fields
 */
export const getMessageFormInitialValues = (message?: MessageDto): MessageFormFields => {
    if (!message) {
        return defaultInitialMessageFormValue;
    }

    return {
        messageTitle: message.messageTitle || '',
        messageDescription: message.messageDescription || '',
        messageType: message.messageType,
        tournament: message.tournament || null,
        promotionMessagePayload: message.promotionMessagePayload || { description: '' },
        articleMessagePayload: message.articleMessagePayload
            ? { ...message.articleMessagePayload, validationStatus: MessageArticleValidationStatus.Idle }
            : { articleEntryId: '', validationStatus: MessageArticleValidationStatus.Idle },
        userGroups: message.messageTarget?.groups || [],
        selectedUsers: message.messageTarget?.users || [],
    };
};

/**
 * Converts form field values to backend MessageDto format
 * Backend expects messageTarget with groups[] and users[] arrays containing objects with only IDs
 */
export const convertMessageFormFieldValuesToDto = (values: MessageFormFields): Partial<MessageDto> => {
    // Create arrays with objects containing only IDs
    const groupIds = values.userGroups.map((group) => group.id) ?? [];
    const userIds = values.selectedUsers.map((user) => user.id) ?? [];

    const articleMessagePayload =
        values.messageType === 'ArticlePushNotification' && values.articleMessagePayload
            ? { articleEntryId: values.articleMessagePayload.articleEntryId }
            : undefined;

    return {
        messageTitle: values.messageTitle,
        messageDescription: values.messageDescription,
        messageType: values.messageType as MessageType,
        tournament: values.tournament || undefined,
        promotionMessagePayload:
            values.messageType === 'PromotionPushNotification' ? values.promotionMessagePayload : undefined,
        articleMessagePayload,
        messageTarget: {
            groupIds,
            userIds,
        },
    };
};

/**
 * Maps backend validation errors to Formik field errors
 * Converts PascalCase property names from backend to camelCase field names for Formik
 * Handles nested properties like "MessageTarget.GroupIds" -> "messageTarget.groupIds"
 */
export const mapBackendValidationErrors = (
    backendErrors: Array<{ property: string; message: string }>,
): Record<string, string> => {
    const fieldErrors: Record<string, string> = {};

    backendErrors.forEach((error) => {
        // Split by dot to handle nested properties (e.g., "MessageTarget.GroupIds")
        const propertyParts = error.property.split('.');

        // Convert each part from PascalCase to camelCase
        const camelCaseParts = propertyParts.map((part) => {
            return part.charAt(0).toLowerCase() + part.slice(1);
        });

        // Rejoin with dots
        const fieldName = camelCaseParts.join('.');
        fieldErrors[fieldName] = error.message;
    });

    return fieldErrors;
};

/**
 * Helper function to get plain text length from HTML string
 * This matches the logic used in ReactQuillWrapper component
 */
export const getPlainTextLength = (html: string): number => {
    if (!html) return 0;
    const plainText = html.replace(/<[^>]*>/g, '');
    return plainText.trim().length;
};

/**
 * Helper function to rebuild the touched object for Formik from an errors object
 */
export const buildTouched = (obj: any): any => {
    if (obj && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            acc[key] = buildTouched(obj[key]);
            return acc;
        }, {} as any);
    }
    return true;
};
